import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppDialogModule } from '@components/dialog/modules/app-dialog.module';
import { PanelToolsModule } from '@components/panel-tools/panel-tools.module';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { FuiButtonComponent, FuiTextAreaComponent, FuiTextInputComponent } from '@fui/fui-components';
import { InterfaceAccessMatrixUnit } from '@modules/login/interfaces/login.interface';
import { FuiDestroyService } from '@services/destroy.service';
import { FormToolsService } from '@services/form-tools.service';
import { LangService } from '@services/lang.service';
import { NotifierService } from '@services/notifier.service';
import { ValidationErrorService } from '@services/validation/validation-error.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { take, takeUntil } from 'rxjs';
import { CatalogsService } from '../../catalogs.service';

interface ICatalogDialog {
    title: string;
    data: any;
    access: InterfaceAccessMatrixUnit;
}

/**
 * Компонент диалога добавления нового элемента рубрикатора
 */
@Component({
    selector: 'app-catalog-dialog',
    templateUrl: './catalog-dialog.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AppDialogModule,
        PanelToolsModule,
        FuiButtonComponent,
        FuiTextInputComponent,
        FuiTextAreaComponent,
    ],
    providers: [CatalogsService, LangService, ValidationErrorService, FormToolsService, FuiDestroyService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class CatalogDialogComponent implements OnInit, AfterViewInit {
    private readonly catalogsService = inject(CatalogsService);
    private readonly destroy$ = inject(FuiDestroyService);
    private readonly dialogRef = inject(MatDialogRef<CatalogDialogComponent>);
    private readonly fb = inject(FormBuilder);
    private readonly formTools = inject(FormToolsService);
    private readonly notifier = inject(NotifierService);
    private readonly spinner = inject(NgxSpinnerService);

    readonly dictionary = inject(LANG_DICTIONARY_TOKEN);
    readonly langService = inject(LangService);
    readonly validationErrorService = inject(ValidationErrorService);
    readonly data: ICatalogDialog = inject(MAT_DIALOG_DATA);

    /**
     * Объект формы
     */
    form: FormGroup = null;

    /**
     * Ссылка на объект формы
     */
    @ViewChild('formRef', { static: true }) formRef: ElementRef;

    /**
     * Группа языковых полей "Название"
     */
    @ViewChildren('langField') langFields: QueryList<FuiTextInputComponent>;

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        this.initForm(this.data.data);
        this.dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    this.dialogRef.close({ save: false });
                }
            });
    }

    /**
     * После отрисовки
     */
    ngAfterViewInit(): void {
        setTimeout(() => {
            const nameField = this.langFields.find((x) => x.fieldName === this.langService.currentLanguage);
            nameField?.inputTextBare?.inputRef?.nativeElement?.focus();
        });
    }

    /**
     * Инициализация формы
     * @param data данные записи
     */
    initForm(data: any) {
        this.form = this.fb.group({
            id: [data?.id ? data.id : null, []],
            parent_id: [data?.parent_id ? data.parent_id : null, []],
            name: this.fb.group({}),
            full_name: this.fb.group({}),
        });

        // Мультиязычные данные
        for (const lang of this.langService.languageNames) {
            const group = this.form.get('name') as FormGroup;
            const validators = [
                ...(this.langService.defaultLanguage === lang ? [Validators.required] : []),
                Validators.maxLength(255),
            ];
            group.addControl(lang, this.fb.control(data.name ? data.name[lang] : '', validators));
        }
        for (const lang of this.langService.languageNames) {
            const group = this.form.get('full_name') as FormGroup;
            group.addControl(lang, this.fb.control(data.full_name ? data.full_name[lang] : '', []));
        }

        this.form.get(`name.${this.langService.currentLanguage}`).setValue(data.valueFromSearch);
    }

    /**
     * Обработка события нажатия на Enter
     */
    onEnterKey($event: KeyboardEvent): void {
        $event.preventDefault();
        if (this.data.access.edit) {
            this.save();
        }
    }

    /**
     * Проверка валидности формы
     */
    protected isValid(): boolean {
        this.formTools.validateAllFormFields(this.form);
        if (this.form.valid) {
            return true;
        }

        if (this.form.get('name').invalid) {
            for (const lang of this.langService.languageNames) {
                if (this.form.get('name.' + lang).invalid) {
                    this.langService.currentLanguage = lang;
                    break;
                }
            }
        }

        this.formTools.focusFirstInvalid(this.formRef);
        return false;
    }

    /**
     * Сохранение данных
     */
    save() {
        if (!this.isValid()) {
            return;
        }

        this.spinner.show();
        this.catalogsService
            .save({ ...this.form.value, is_folder: 0 })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (result: any) => {
                    this.spinner.hide();
                    if (result.is_error) {
                        this.notifier.error(result.data?.valid_message ?? result.message);
                        return;
                    }
                    this.dialogRef.close({
                        save: true,
                        data: result.data,
                    });
                },
                error: (err: unknown) => {
                    this.spinner.hide();
                    this.notifier.exception(err);
                },
            });
    }

    /**
     * Закрытие диалога
     */
    close() {
        this.dialogRef.close({ save: false });
    }
}
